import ClockIcons from '@/icons/ClockIcons'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TimeRange {
    start: string // Format: "HH:mm"
    end: string // Format: "HH:mm"
}

interface TimePickerInputProps {
    value?: string // Format: "HH:mm"
    onChange?: (value: string) => void
    placeholder?: string
    disabled?: boolean
    className?: string
    label?: string
    error?: string
    disabledTimeRanges?: TimeRange[] // Array of time ranges to disable
}

const TimePickerInput: React.FC<TimePickerInputProps> = ({
    value = '',
    onChange,
    placeholder = '00:00',
    disabled = false,
    className = '',
    error,
    disabledTimeRanges = [],
}) => {
    console.log(disabledTimeRanges, 'disabledTimeRanges')
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const [inputValue, setInputValue] = useState(value)
    const [selectedHour, setSelectedHour] = useState(0)
    const [selectedMinute, setSelectedMinute] = useState(0)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    // Helper function to convert time string to minutes
    const timeToMinutes = (time: string): number => {
        const [hours, minutes] = time.split(':').map(Number)
        return hours * 60 + minutes
    }

    // Helper function to check if a time is within disabled ranges
    const isTimeDisabled = (hour: number, minute: number): boolean => {
        const currentTimeInMinutes = hour * 60 + minute

        return disabledTimeRanges.some((range) => {
            const startMinutes = timeToMinutes(range.start)
            const endMinutes = timeToMinutes(range.end)
            return (
                currentTimeInMinutes >= startMinutes &&
                currentTimeInMinutes <= endMinutes
            )
        })
    }

    // Helper function to check if an entire hour should be disabled
    const isHourDisabled = (hour: number): boolean => {
        // Check if ALL minutes (0-59) in this hour are disabled
        for (let minute = 0; minute < 60; minute++) {
            if (!isTimeDisabled(hour, minute)) {
                return false // If any minute is available, don't disable the hour
            }
        }
        return true // All minutes are disabled, so disable the hour
    }

    // Parse initial value
    useEffect(() => {
        if (value) {
            const [hour, minute] = value.split(':').map(Number)
            if (!isNaN(hour) && !isNaN(minute)) {
                setSelectedHour(hour)
                setSelectedMinute(minute)
                setInputValue(value)
            }
        }
    }, [value])

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Format number to 2 digits
    const formatNumber = (num: number): string =>
        num.toString().padStart(2, '0')

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let inputVal = e.target.value

        // Remove non-numeric characters except ':'
        inputVal = inputVal.replace(/[^\d:]/g, '')

        // Auto-add colon after 2 digits
        if (inputVal.length === 2 && !inputVal.includes(':')) {
            inputVal += ':'
        }

        // Limit to HH:mm format
        if (inputVal.length > 5) {
            inputVal = inputVal.slice(0, 5)
        }

        // Real-time validation for hours and minutes
        if (inputVal.includes(':')) {
            const parts = inputVal.split(':')
            const hourPart = parts[0]
            const minutePart = parts[1]

            // Validate hour part (max 23)
            if (hourPart.length === 2) {
                const hour = parseInt(hourPart)
                if (hour > 23) {
                    inputVal = '23:' + minutePart
                }
            }

            // Validate minute part (max 59)
            if (minutePart && minutePart.length >= 1) {
                // Check if first digit of minute is > 5
                const firstMinuteDigit = parseInt(minutePart[0])
                if (firstMinuteDigit > 5) {
                    inputVal = hourPart + ':5'
                } else if (minutePart.length === 2) {
                    const minute = parseInt(minutePart)
                    if (minute > 59) {
                        inputVal = hourPart + ':59'
                    }
                }
            }
        }

        setInputValue(inputVal)

        // Validate and update if complete
        if (inputVal.length === 5 && inputVal.includes(':')) {
            const [hour, minute] = inputVal.split(':').map(Number)
            if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
                setSelectedHour(hour)
                setSelectedMinute(minute)
                onChange?.(inputVal)
            }
        }
    }

    // Handle input blur
    const handleInputBlur = () => {
        if (inputValue.length === 5) {
            const [hour, minute] = inputValue.split(':').map(Number)
            if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
                const formattedValue = `${formatNumber(hour)}:${formatNumber(minute)}`
                setInputValue(formattedValue)
                onChange?.(formattedValue)
            } else {
                // Reset to previous valid value or empty
                setInputValue(value)
            }
        } else if (inputValue.length === 0) {
            onChange?.('')
        }
    }

    // Handle hour/minute selection
    const handleTimeSelect = (hour: number, minute: number) => {
        setSelectedHour(hour)
        setSelectedMinute(minute)
        const formattedValue = `${formatNumber(hour)}:${formatNumber(minute)}`
        setInputValue(formattedValue)
        onChange?.(formattedValue)
        setIsOpen(false)
    }

    // Generate hour options
    const hours = Array.from({ length: 24 }, (_, i) => i)
    const minutes = Array.from({ length: 60 }, (_, i) => i)

    return (
        <div className={`relative ${className}`}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={`
                        h-[36px] rounded-[4px]
                        w-full px-3 py-2 pr-10 text-sm border
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                        ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : 'bg-white'}
                        ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'}
                    `}
                />

                {/* Icons */}
                <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                        type="button"
                        onClick={() => !disabled && setIsOpen(!isOpen)}
                        disabled={disabled}
                        className={`p-2 text-gray-400 hover:text-gray-600 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                        <ClockIcons
                            className="w-4 h-4 text-primary"
                            color={disabled ? 'gray' : 'primary'}
                        />
                    </button>
                </div>

                {/* Dropdown */}
                {isOpen && !disabled && (
                    <div
                        ref={dropdownRef}
                        className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50"
                    >
                        <div className="flex max-h-60">
                            {/* Hours */}
                            <div className="flex-1 border-r border-gray-200">
                                <div className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 text-center">
                                    {t('hours')}
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    {hours.map((hour) => (
                                        <button
                                            key={hour}
                                            type="button"
                                            onClick={() =>
                                                !isHourDisabled(hour) &&
                                                handleTimeSelect(
                                                    hour,
                                                    selectedMinute
                                                )
                                            }
                                            className={`
                                                w-full px-3 py-1 text-sm text-left hover:bg-blue-50
                                                ${selectedHour === hour ? 'bg-blue-100 text-blue-600 font-medium' : 'text-gray-700'}
                                                ${isHourDisabled(hour) ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}
                                            `}
                                            disabled={isHourDisabled(hour)}
                                        >
                                            {formatNumber(hour)}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Minutes */}
                            <div className="flex-1">
                                <div className="px-2 py-1 text-xs font-medium text-gray-500 bg-gray-50 text-center">
                                    {t('minutes')}
                                </div>
                                <div className="max-h-48 overflow-y-auto">
                                    {minutes.map((minute) => (
                                        <button
                                            key={minute}
                                            type="button"
                                            onClick={() =>
                                                !isTimeDisabled(
                                                    selectedHour,
                                                    minute
                                                ) &&
                                                handleTimeSelect(
                                                    selectedHour,
                                                    minute
                                                )
                                            }
                                            className={`
                                                w-full px-3 py-1 text-sm text-left hover:bg-blue-50
                                                ${selectedMinute === minute ? 'bg-blue-100 text-blue-600 font-medium' : 'text-gray-700'}
                                                ${isTimeDisabled(selectedHour, minute) ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''}
                                            `}
                                            disabled={isTimeDisabled(
                                                selectedHour,
                                                minute
                                            )}
                                        >
                                            {formatNumber(minute)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    )
}

export default TimePickerInput
